import React, { useCallback, useEffect, useMemo } from 'react';

import { mString } from 'lib/helpers/utility';
import { useExportContext } from 'lib/context/ExportContext/ExportContext';

import QrCode from 'components/shared/QrCode/QrCode';

import { MemoComponent } from '../lib/external/configs';

import { getInvoiceQrCode } from './lib/generateInvoiceQrCode';

const NewInvoiceView = ({ exportId, invoiceDetails, hasClaims, token }) => {
  const {
    tax = 0,
    tax_amount_cents = 0,
    prevDiscount = 0,
    charge = 0,
    discount = 0,
    amount_paid = 0,
    ins_payment = 0,
    ins_adjustment = 0,
    surcharge_amount = 0,
    write_off = 0,
    memo = null,
    aeob: isAEOB = null,
    ptBalance = 0,
    totalAmount = 0,
    paid = false,
    other_adj = 0
  } = invoiceDetails || {};

  const { setFooterProps } = useExportContext();

  let total =
    charge -
    ins_payment -
    ins_adjustment -
    prevDiscount -
    amount_paid +
    tax -
    write_off -
    other_adj;

  if (!isAEOB) total = other_adj ? charge - other_adj : ptBalance;

  const subTotal = total - discount;

  let amountDue = paid ? subTotal : subTotal + surcharge_amount;

  if (subTotal > 0) amountDue += tax_amount_cents;

  const qrCode = useMemo(() => {
    if (token) {
      return `${process.env.REACT_APP_BASE_URL}/invoice/${token}`;
    }

    return null;
  }, [token]);

  const updateFooterProps = useCallback(async () => {
    const qrCode = await getInvoiceQrCode(token);
    setFooterProps(exportId, {
      invoice: { ...invoiceDetails, total, finalDiscount: discount, amountDue, qrCode },
      isAEOB,
      hasClaims
    });
  }, [exportId, isAEOB, hasClaims, JSON.stringify(invoiceDetails), total, discount, amountDue]);

  useEffect(() => {
    updateFooterProps();
  }, [updateFooterProps]);

  useEffect(() => {
    setFooterProps(exportId, {
      invoice: { ...invoiceDetails, total, finalDiscount: discount, amountDue, qrCode },
      isAEOB,
      hasClaims
    });
  }, [exportId, isAEOB, hasClaims, JSON.stringify(invoiceDetails), total, discount, amountDue]);

  return (
    <>
      {memo && (
        <div className="pt- m-3 items-center rounded bg-gradient-to-r from-primary-25 to-[#004f6b0d] p-5">
          <div className="mb-3 text-lg font-semibold">Memo</div>
          <MemoComponent value={memo} />
        </div>
      )}
      <div className="relative z-20 m-[16px_-16px_-16px_-16px] flex min-h-[40px] items-center !gap-2 bg-gradient-to-r from-[#004F6B3D] to-[#004f6b0d] !px-4 py-3 uppercase text-primary-900">
        <div className="my-4 w-full px-8">
          <div className="mx-3">
            <div className="mb-4">
              {isAEOB ? (
                <>
                  <div className="mt-3 flex justify-between">
                    <span>Billed</span>
                    {mString(charge)}
                  </div>

                  {hasClaims && (
                    <>
                      <div className="mt-3 flex justify-between">
                        <span>Insurance covered</span>
                        <span>
                          {ins_payment > 0 ? '-' : ''}
                          {mString(ins_payment)}
                        </span>
                      </div>

                      <div className="mt-3 flex justify-between">
                        <span>Insurance adjusted</span>
                        <span>
                          {ins_adjustment > 0 ? '-' : ''}
                          {mString(ins_adjustment)}
                        </span>
                      </div>
                    </>
                  )}
                  {other_adj > 0 && (
                    <div className="mt-3 flex justify-between">
                      <span>Other adjustments</span>
                      <span>
                        {other_adj > 0 ? '-' : ''}
                        {mString(other_adj)}
                      </span>
                    </div>
                  )}
                </>
              ) : !isAEOB || other_adj ? (
                <>
                  <div className="mt-3 flex justify-between">
                    <span>Billed</span>
                    {mString(charge)}
                  </div>

                  {other_adj > 0 && (
                    <div className="mt-3 flex justify-between">
                      <span>Other adjustments</span>
                      <span>
                        {other_adj > 0 ? '-' : ''}
                        {mString(other_adj)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-3 flex justify-between">
                  <span>Billed</span>
                  {mString(totalAmount)}
                </div>
              )}

              {amount_paid > 0 && (
                <div className="mt-3 flex justify-between">
                  <span>Patient paid</span>
                  <span>-{mString(amount_paid)}</span>
                </div>
              )}

              {prevDiscount > 0 && (
                <div className="mt-3 flex justify-between">
                  <span>Discount</span>
                  <span>-{mString(prevDiscount)}</span>
                </div>
              )}

              {write_off > 0 && (
                <div className="mt-3 flex justify-between">
                  <span>Write-off</span>
                  <span>-{mString(write_off)}</span>
                </div>
              )}

              {tax > 0 && (
                <div className="mt-3 flex justify-between">
                  <span>Tax</span>
                  <span>{mString(tax)}</span>
                </div>
              )}

              <hr className="bg-gray-300 my-8 h-px border-0" />

              <div className="flex justify-between border-t pt-3">
                <span className="font-bold">Subtotal</span>
                <span className="text-lg font-bold">{mString(total)}</span>
              </div>

              <div className="mt-3 flex justify-between">
                <span>Additional discount</span>
                <span>{mString(discount)}</span>
              </div>

              {surcharge_amount > 0 && (
                <div className="mt-3 flex justify-between">
                  <span>Technology fee</span>
                  <span>{mString(surcharge_amount)}</span>
                </div>
              )}
            </div>
            <div className="mt-3 flex justify-between border-t pt-3">
              <span className="font-bold">Amount due</span>
              <span className="text-2xl font-bold">{mString(amountDue)}</span>
            </div>
          </div>
          {token && qrCode && (
            <div className="mx-3 mt-5 flex flex-col items-center gap-x-2">
              <span className="mb-1 text-xs font-600 text-primary-900">Scan and pay</span>
              <QrCode value={qrCode} size={80} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewInvoiceView;
